import { FunctionComponent, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Icon,
  Link,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { pageMapping } from "../../builder/mapping/page";
import { builtfirstCommunitySubdomain } from "../../helpers/navigation";
import { useConfiguration } from "../../dataAccess/api/configuration";
import TextField from "./TextField";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import { CloseButton } from "./CloseButton";
import { ButtonProgress } from "./ButtonProgress";
import SSOIntegration from "./SSOIntegration";
import TermsText from "./TermsText";
import { LoginSettings } from "../pages/LogIn";
import {
  COMPANY_NAME_PLACEHOLDER_TEXT,
  CONFIRM_PASSWORD_PLACEHOLDER_TEXT,
  DEFAULT_SIGN_IN_VALUE,
  DEFAULT_SIGN_UP_VALUE,
  EMAIL_ADDRESS_PLACEHOLDER_TEXT,
  EXISTING_ACCOUNT_LABEL,
  FULL_NAME_PLACEHOLDER_TEXT,
  PASSWORD_PLACEHOLDER_TEXT,
  SKIP_BUTTON_LABEL,
  SUBSCRIBE_NEWSLETTER_LABEL,
} from "../../../theme/defaultConstants";
import { SignupSettings } from "../pages/SignUp";
import { replaceBracesKey } from "../../helpers/formatter";
import { useTranslation } from "../../helpers/i18n";
import { useForm } from "../../helpers/useForm";

interface FormData {
  fullName: string;
  signUpOrganizationName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  termsAndConditions: boolean;
  subscribeToNewsletter: boolean;
}

interface Props {
  onSubmit: (data: FormData) => void;
  loading?: boolean;
  signUpErrors: Map<SignUpAttribute, string>;
  serverError?: string;
  isInvite?: boolean;
  settings: SignupSettings;
  setOpenLoginInProfilePopUp?: () => void;
  closePopUp?: () => void;
  clearBackendError?: (field: SignUpAttribute) => void;
}

const getStyledFormMaxWidth = (isOnPopup?: boolean) => {
  if (isOnPopup) {
    return "35rem";
  }

  return "auto";
};

export const StyledForm = styled("form")<{ isOnPopup?: boolean }>(({
  theme,
  isOnPopup,
}) => {
  const isCenterAlignment = theme.settings.lockScreens.alignment === "center";
  const padding = "4.3rem";

  return {
    padding: isOnPopup ? "1.4rem" : "2.5rem",
    alignSelf: "center",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    [theme.breakpoints.up("md")]: {
      paddingLeft: padding,
      paddingRight: padding,
      width: getStyledFormMaxWidth(isOnPopup),
      minHeight: isCenterAlignment ? "75%" : "auto",
      justifyContent: isCenterAlignment ? "center" : "normal",
      borderRadius: isCenterAlignment ? "0.6rem" : "0",
    },
  };
});

export const FormSection = styled("div")(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

export const FormContent = styled("div")<{
  isOnPopup?: boolean;
}>(({ theme, isOnPopup }) => ({
  width: "100%",
  maxWidth: isOnPopup
    ? undefined
    : Number(theme.settings.lockScreens.maxWidth || 800),
  alignSelf: "center",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  paddingLeft: "1rem",
}));

const StyledLink = styled(Link)(() => ({
  cursor: "pointer",
}));

export const StyledFormTitle = styled(Typography)<{
  subtitle?: boolean;
}>(({ theme, subtitle }) => ({
  alignSelf: "center",
  fontSize: theme.typography[subtitle ? "body1" : "h2"].fontSize,
}));

const SignUpLink = styled(Typography)(() => ({
  alignSelf: "center",
  marginTop: "1rem",
}));

const CheckboxLabel = styled(Typography)(({ theme }) => ({
  lineHeight: "1.3",
  fontSize: theme.typography.body2.fontSize,
}));

export const SIGN_UP_ATTRIBUTES = [
  "password",
  "email",
  "fullName",
  "signUpOrganizationName",
];

export type SignUpAttribute =
  | "password"
  | "email"
  | "fullName"
  | "signUpOrganizationName";

export function isSignUpAttribute(value: string): value is SignUpAttribute {
  return SIGN_UP_ATTRIBUTES.includes(value);
}

const SignUpForm: FunctionComponent<Props> = ({
  loading,
  onSubmit,
  serverError,
  signUpErrors = new Map(),
  settings: componentSettings,
  isInvite,
  setOpenLoginInProfilePopUp,
  closePopUp,
  clearBackendError,
}: Props) => {
  const {
    label,
    sublabel,
    companyNamePlaceholderText,
    confirmPasswordPlaceholderText,
    emailAddressPlaceholderText,
    existingAccountLabel,
    fullNamePlaceholderText,
    orLabel,
    subscribeNewsletterLabel,
    passwordPlaceholderText,
    skipButtonLabel,
    buttonLabel,
  } = componentSettings || {};
  const { t } = useTranslation();

  const baseSchema = useMemo(
    () =>
      yup.object().shape({
        fullName: yup
          .string()
          .required(t("information_user_full_name_required")),
        password: yup
          .string()
          .required(t("information_password_required"))
          .min(
            12,
            replaceBracesKey(t("information_password_min_length"), {
              key: "min_length",
              value: "12",
            }),
          ),
        passwordConfirmation: yup
          .string()
          .required(t("information_password_required"))
          .oneOf(
            [yup.ref("password"), null],
            t("information_password_must_match"),
          ),
        subscribeToNewsletter: yup.boolean(),
      }),
    [t],
  );

  const fullFieldsSchema = useMemo(
    () =>
      baseSchema.shape({
        email: yup
          .string()
          .transform((value) => value.trim())
          .email(t("information_email_valid"))
          .required(t("information_email_required")),
        signUpOrganizationName: yup
          .string()
          .required(t("information_organization_name_required")),
      }),
    [baseSchema, t],
  );
  const { configuration } = useConfiguration();
  const { settings } = useTheme();
  const showTermsAndConditions =
    !configuration?.customDomain || settings?.termsAndConditions;
  const validationSchema = useMemo(() => {
    let schema = isInvite ? baseSchema : fullFieldsSchema;

    if (showTermsAndConditions) {
      schema = schema.shape({
        termsAndConditions: yup
          .boolean()
          .oneOf([true], t("information_terms_and_conditons")),
      });
    }

    return schema;
  }, [isInvite, showTermsAndConditions, baseSchema, fullFieldsSchema, t]);

  const { control, formState, handleSubmit } = useForm<FormData>({
    skipDefaultValidationFor: ["password", "passwordConfirmation"],
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fullName: "",
      signUpOrganizationName: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      termsAndConditions: false,
      subscribeToNewsletter: false,
    },
  });

  const loginSettings = useMemo(
    () =>
      configuration?.publishedTemplate?.properties.pages.find(
        ({ id }) => id === "Login",
      )?.settings as LoginSettings,
    [configuration],
  );

  const loginButtonLabel = loginSettings?.label || DEFAULT_SIGN_IN_VALUE;
  const title = label || DEFAULT_SIGN_UP_VALUE;

  const handleFieldChange =
    (fieldName: SignUpAttribute, onChange: (value: any) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (signUpErrors.has(fieldName) && clearBackendError) {
        clearBackendError(fieldName);
      }
      onChange(event.target.value);
    };

  return (
    <StyledForm
      isOnPopup={!!setOpenLoginInProfilePopUp}
      onSubmit={handleSubmit(onSubmit)}
      aria-label={title}
    >
      {closePopUp && (
        <CloseButton onClick={closePopUp}>
          <Icon>close</Icon>
        </CloseButton>
      )}
      <FormContent isOnPopup={!!setOpenLoginInProfilePopUp}>
        <StyledFormTitle variant="h1">{title}</StyledFormTitle>
        {sublabel && <StyledFormTitle subtitle>{sublabel}</StyledFormTitle>}
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <SSOIntegration orLabel={orLabel} />
        <FormSection>
          <Controller
            name="fullName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={handleFieldChange("fullName", field.onChange)}
                label={fullNamePlaceholderText || FULL_NAME_PLACEHOLDER_TEXT}
                fullWidth
                error={
                  !!formState.errors.fullName || signUpErrors.has("fullName")
                }
                helperText={
                  formState.errors.fullName?.message ||
                  signUpErrors.get("fullName")
                }
              />
            )}
          />
          {!isInvite && (
            <Controller
              name="signUpOrganizationName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={handleFieldChange(
                    "signUpOrganizationName",
                    field.onChange,
                  )}
                  label={
                    companyNamePlaceholderText || COMPANY_NAME_PLACEHOLDER_TEXT
                  }
                  fullWidth
                  error={
                    !!formState.errors.signUpOrganizationName ||
                    signUpErrors.has("signUpOrganizationName")
                  }
                  helperText={
                    formState.errors.signUpOrganizationName?.message ||
                    signUpErrors.get("signUpOrganizationName")
                  }
                />
              )}
            />
          )}
          {!isInvite && (
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={handleFieldChange("email", field.onChange)}
                  label={
                    emailAddressPlaceholderText ||
                    EMAIL_ADDRESS_PLACEHOLDER_TEXT
                  }
                  fullWidth
                  error={!!formState.errors.email || signUpErrors.has("email")}
                  helperText={
                    formState.errors.email?.message || signUpErrors.get("email")
                  }
                />
              )}
            />
          )}
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={handleFieldChange("password", field.onChange)}
                label={passwordPlaceholderText || PASSWORD_PLACEHOLDER_TEXT}
                type="password"
                fullWidth
                error={
                  !!formState.errors.password || signUpErrors.has("password")
                }
                helperText={
                  formState.errors.password?.message ||
                  signUpErrors.get("password")
                }
              />
            )}
          />
          <Controller
            name="passwordConfirmation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  confirmPasswordPlaceholderText ||
                  CONFIRM_PASSWORD_PLACEHOLDER_TEXT
                }
                type="password"
                fullWidth
                error={!!formState.errors.passwordConfirmation}
                helperText={formState.errors.passwordConfirmation?.message}
              />
            )}
          />
          {showTermsAndConditions && (
            <Controller
              name="termsAndConditions"
              control={control}
              render={({ field }) => (
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <CheckboxLabel>
                        <TermsText />
                      </CheckboxLabel>
                    }
                  />
                  {formState.errors.termsAndConditions && (
                    <StyledFormHelperText error>
                      {formState.errors.termsAndConditions.message}
                    </StyledFormHelperText>
                  )}
                </div>
              )}
            />
          )}
          {!configuration?.customDomain &&
            configuration?.subdomain === builtfirstCommunitySubdomain && (
              <Controller
                name="subscribeToNewsletter"
                control={control}
                render={({ field }) => (
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        <CheckboxLabel>
                          {subscribeNewsletterLabel ||
                            SUBSCRIBE_NEWSLETTER_LABEL}
                        </CheckboxLabel>
                      }
                    />
                    {formState.errors.subscribeToNewsletter && (
                      <StyledFormHelperText error>
                        {formState.errors.subscribeToNewsletter.message}
                      </StyledFormHelperText>
                    )}
                  </div>
                )}
              />
            )}
          <ThemeButton
            startIcon={loading ? <ButtonProgress /> : null}
            variant="contained"
            type="submit"
            disabled={loading}
          >
            {buttonLabel || DEFAULT_SIGN_UP_VALUE}
          </ThemeButton>
          {configuration?.isOpenMarketplace && !setOpenLoginInProfilePopUp && (
            <ThemeButton href={pageMapping.PLP.path} variant="text" fullWidth>
              {skipButtonLabel || SKIP_BUTTON_LABEL}
            </ThemeButton>
          )}
        </FormSection>
        <SignUpLink>
          {replaceBracesKey(existingAccountLabel || EXISTING_ACCOUNT_LABEL, {
            key: "sign_in",
            value: setOpenLoginInProfilePopUp ? (
              <StyledLink onClick={setOpenLoginInProfilePopUp} tabIndex={0}>
                {loginButtonLabel}
              </StyledLink>
            ) : (
              <StyledLink href={pageMapping.Login.path} tabIndex={0}>
                {loginButtonLabel}
              </StyledLink>
            ),
          })}
        </SignUpLink>
      </FormContent>
    </StyledForm>
  );
};

export default SignUpForm;
